import React from 'react'
import styled from 'styled-components'
import FormatCurrency from 'components/Common/FormatCurrency'
import LinePlaneFlyIcon from 'components/Luxkit/Icons/line/LinePlaneFlyIcon'
import useDefaultAirport from 'hooks/useDefaultAirport'
import Caption from 'components/Luxkit/Typography/Caption'
import getValueOffPercent from 'lib/offer/getValueOffPercent'
import Pill from 'components/Luxkit/Pill'
import TextButton from 'components/Luxkit/Button/TextButton'
import Group from 'components/utils/Group'
import PaletteProvider from 'contexts/PaletteProvider'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import PriceRowTaxesAndFees from 'components/Luxkit/PricePoints/PriceRowTaxesAndFees'
import CaptionTextBlock from 'components/Luxkit/TextBlocks/CaptionTextBlock'
import { ValueWrapper } from 'components/Common/OfferPricing/styles'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import PriceRowPriceCaption from 'components/Luxkit/PricePoints/PriceRowPriceCaption'
import PriceRowPrice from 'components/Luxkit/PricePoints/PriceRowPrice'

const Pricing = styled.div`
  display: flex;
  flex-direction: column;
`

interface Props {
  offer: App.Offer | App.OfferSummary;
  pkg: App.Package;
}

function HeroOfferTileBundlePricing(props: Props) {
  const { offer, pkg } = props
  const defaultAirport = useDefaultAirport()

  const flightPrice = offer.flightPrices[defaultAirport?.code]

  if (!flightPrice) {
    return (
      <Pricing>
        <CaptionTextBlock colour="neutral-one" variant="medium" weight="bold" startIcon={<LinePlaneFlyIcon colour="neutral-one" />}>
          Hotel + Flights packages
        </CaptionTextBlock>
      </Pricing>
    )
  }

  const totalPrice = pkg.price + flightPrice
  const totalValue = pkg.value + flightPrice
  const showValue = pkg.shouldDisplayValue && totalValue > totalPrice

  const valueOffPercent = getValueOffPercent(totalValue, totalPrice)
  const shouldShowRate = (valueOffPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE) && !offer.isDiscountPillHidden

  return (
    <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="end" gap={4}>
      <Group direction="vertical">
        <CaptionTextBlock colour="neutral-one" variant="medium" weight="bold" startIcon={<LinePlaneFlyIcon colour="neutral-one" />}>
          Hotel + Flights
        </CaptionTextBlock>
        <PriceRowPriceCaption>{offer.tileDurationLabel} from</PriceRowPriceCaption>
        <PriceRowPrice
          size="L"
          price={Math.ceil(totalPrice)}
          saleUnit="package"
          taxesAndFees={pkg.taxesAndFees}
        />
        <PriceRowTaxesAndFees
          isTaxExclusiveRegion={offer.property?.taxesPayableAtProperty}
          taxesAndFees={pkg.taxesAndFees}
        />
        {showValue && (
          <ValueWrapper variant="large" weight="bold" colour="neutral-one">
            Valued up to <FormatCurrency value={Math.ceil(totalValue)} />
            {shouldShowRate &&
              <PaletteProvider type="default">
                <Pill kind="primary" variant="success">-{valueOffPercent}%</Pill>
              </PaletteProvider>
            }
          </ValueWrapper>
        )}
        <Caption variant="medium" colour="neutral-three">
          2 adults
        </Caption>
      </Group>
      <CSSBreakpoint min="desktop">
        <TextButton kind="primary">
          View offer
        </TextButton>
      </CSSBreakpoint>
    </Group>
  )
}

export default HeroOfferTileBundlePricing
